import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams, useQuery } from 'react-router-dom';
import CustomPaperBox from '../../../components/common/CustomPaperBox';
import CustomChip from '../../../components/common/CustomChip';
import BasicDetailsForm from './BasicDetailsForm';
import BankDetailsForm from './BankDetailsForm';
import AdvisorConditionsPage from "../../common/AdvisorConditionsPage";
import OtpVerificationModal from './VerifyOtpModal';
import UploadModal from './UploadFileModal';
import { toast } from 'react-toastify';
import UserSelection from './UserSelection';
import axios from 'axios';
import { advisorRegistration, sendMobileOtp, sendEmailOtp, verifyMobileOtp, verifyEmailOtp, advisorReSubmitUrl } from '../../../utils/urls';
import FormSubmissionPage from './FormSubmissionPage';
import { useSelector } from 'react-redux';
import { errorToast, successToast } from '../../../utils/toasts';

const OnboardingForm = () => {
  const loggedInUser = useSelector((state) => state.auth);
  const [step, setStep] = useState(1);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpType, setOtpType] = useState(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [verifiedFields, setVerifiedFields] = useState({ mobile_number: false, email: false });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadField, setUploadField] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([null, null]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [verifyErrors, setVerifyErrors] = useState({ mobile_number: '', email: '' });
  const [otpToken, setOtpToken] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    mobile_number: false,
    email: false,
    aadhar_card_number: false,
    pan_card_number: false,
  });
  const [uploadedDocs, setUploadedDocs] = useState({
    aadhar_card_number: false,
    pan_card_number: false,
  });
  const formikRef = useRef(null);
  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    join_as: '',
    name: '',
    mobile_number: '',
    email: '',
    aadhar_card_number: '',
    pan_card_number: '',
    qualification: '',
    bank_ifsc_code: '',
    bank_name: '',
    bank_branch: '',
    bank_account_number: '',
    front_aadhar_card_file: null,
    back_aadhar_card_file: null,
    front_pan_card_file: null,
    back_pan_card_file: null,
  });

  const [otpResendKey, setOtpResendKey] = useState(Date.now());

  useEffect(() => {
    if (otpModalOpen || uploadModalOpen) {
      setLoadingStates({
        mobile_number: false,
        email: false,
        aadhar_card_number: false,
        pan_card_number: false,
      });
    }
  }, [otpModalOpen, uploadModalOpen]);

  useLayoutEffect(() => {
    console.log(loggedInUser);
    
    if (loggedInUser?.isSkipLogin && loggedInUser?.isAuthenticated) {
      if (loggedInUser?.user?.mobile_number) {
        formikRef?.current?.setFieldValue('mobile_number', loggedInUser?.user?.mobile_number);
        formikRef?.current?.setTouched({ mobile_number: true });
        setMobileNumber(loggedInUser?.user?.mobile_number);
        setVerifiedFields({ mobile_number: true, email: false });
      }
    } else if (loggedInUser?.user?.advisor_onboarding_status_id?.title === "Rejected") {
      if (loggedInUser?.user) {
        const user = loggedInUser.user;

        formikRef?.current?.setFieldValue("name", user.name || '');
        formikRef?.current?.setFieldValue("qualification", user.qualification || '');
        formikRef?.current?.setFieldValue("mobile_number", user.mobile_number || '');
        formikRef?.current?.setFieldValue("email", user.email || '');
        formikRef?.current?.setFieldValue("aadhar_card_number", user.aadhar_card_number || '');
        formikRef?.current?.setFieldValue("pan_card_number", user.pan_card_number || '');

        formikRef?.current?.setFieldValue("bank_ifsc_code", user.bank_details?.[0]?.bank_ifsc_code || '');
        formikRef?.current?.setFieldValue("bank_name", user.bank_details?.[0]?.bank_name || '');
        formikRef?.current?.setFieldValue("bank_branch", user.bank_details?.[0]?.bank_branch || '');
        formikRef?.current?.setFieldValue("bank_account_number", user.bank_details?.[0]?.bank_account_number || '');

        // Handle image fields
        const imageFields = [
          { key: 'front_aadhar_card_file', url: user.front_aadhar_card_url },
          { key: 'back_aadhar_card_file', url: user.back_aadhar_card_url },
          { key: 'front_pan_card_file', url: user.front_pan_card_url },
          { key: 'back_pan_card_file', url: user.back_pan_card_url },
        ];

        const updatedSelectedFiles = [...selectedFiles];

        imageFields.forEach(({ key, url }, index) => {
          if (url) {
            const fileObj = { preview: url, file: null };
            formikRef?.current?.setFieldValue(key, url);

            // Aadhar: 0,1; PAN: 2,3
            if (key.includes("aadhar")) {
              updatedSelectedFiles[index] = fileObj;
            } else if (key.includes("pan")) {
              updatedSelectedFiles[index] = fileObj;
            }
          }
        });

        setSelectedFiles(updatedSelectedFiles);
        setUploadedDocs({
          aadhar_card_number: !!user.front_aadhar_card_url && !!user.back_aadhar_card_url,
          pan_card_number: !!user.front_pan_card_url && !!user.back_pan_card_url,
        });

        setMobileNumber(user.mobile_number || '');
        setEmailAddress(user.email || '');
        setVerifiedFields({ mobile_number: true, email: true });

        const _rejection_remark = user.rejection_remark?.[user.rejection_remark.length - 1];

        const FieldConfig = {
          name: "name",
          email: "email",
          mobile: "mobile_number",
          panCard: "pan_card_number",
          qualification: "qualification",
          adharCard: "aadhar_card_number",
          accountNumber: "bank_account_number",
          bankName: "bank_name",
          branch: "bank_branch",
          ifscCode: "bank_ifsc_code",
          adharCardFront: "front_aadhar_card_file",
          adharCardBack: "back_aadhar_card_file",
          panCardFront: "front_pan_card_file",
          panCardBack: "back_pan_card_file",
        };

        _rejection_remark?.reason_type?.forEach((element) => {
          const fieldKey = FieldConfig[element];
          if (fieldKey) {
            formikRef?.current?.setFieldValue(fieldKey, "");
            formikRef?.current?.setFieldError(fieldKey, "This info was rejected.");
            formikRef?.current?.setFieldTouched(fieldKey, true);
          }
        });
      }
    } else {
      setVerifiedFields({ mobile_number: false, email: false });
    }
  }, [loggedInUser])

  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file && file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [selectedFiles]);

  const getAuthHeaders = () => {
    const token = otpToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleVerifyClick = async (type, value) => {
    const fieldName = type === 'mobile' ? 'mobile_number' : 'email';
    setLoadingStates((prev) => ({ ...prev, [fieldName]: true }));
    if (!value) {
      setVerifyErrors((prev) => ({
        ...prev,
        [fieldName]: `Please enter a valid ${type}`,
      }));
      setLoadingStates((prev) => ({ ...prev, [fieldName]: false }));
      return;
    }

    setVerifyErrors((prev) => ({
      ...prev,
      [fieldName]: '',
    }));

    try {
      const isMobile = type === 'mobile';
      const endpoint = isMobile ? sendMobileOtp : sendEmailOtp;
      const payload = isMobile
        ? { mobile_number: value, purpose_for: 'registration' }
        : { email: value };
      const response = await axios.post(endpoint, payload, {
        headers: getAuthHeaders(),
      });
      const token = response.data?.data?.token;
      if (!token) throw new Error('Token not received from server');

      setOtpToken(token);
      setOtpType(type);
      if (isMobile) setMobileNumber(value);
      else setEmailAddress(value);
      setOtpModalOpen(true);
    } catch (error) {
      console.error(`Failed to send ${type} OTP:`, error);
      const errorMessage =
        error.response?.data?.error?.message ||
        (error.response?.status === 401
          ? 'Authentication failed: Please log in again'
          : 'Failed to send OTP');
      if (error.response?.status === 401) navigate('/login');
      setVerifyErrors((prev) => ({
        ...prev,
        [fieldName]: errorMessage,
      }));
      errorToast(errorMessage);
      setLoadingStates((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleOtpSubmit = async (otp) => {
    try {
      if (!otpToken) throw new Error('No OTP token available');

      const isMobile = otpType === 'mobile';
      const endpoint = isMobile ? verifyMobileOtp : verifyEmailOtp;
      const payload = isMobile
        ? { token: otpToken, otp, mobile_number: mobileNumber, purpose_for: 'registration' }
        : { token: otpToken, otp, email: emailAddress };
      const response = await axios.post(endpoint, payload, {
        headers: getAuthHeaders(),
      });
      if (response.data.success) {
        setVerifiedFields((prev) => ({ ...prev, [otpType]: true }));
        setOtpModalOpen(false);
        successToast('OTP verified successfully');
      } else {
        throw new Error(response.data.error?.message || 'Invalid OTP');
      }
    } catch (error) {
      console.error('OTP verification failed:', error);
      const errorMessage =
        error.response?.data?.error?.message ||
        (error.response?.status === 401
          ? 'Authentication failed: Please log in again'
          : 'OTP verification failed');
      if (error.response?.status === 401) navigate('/login');
      errorToast(errorMessage);
    }
  };

  const handleOtpResend = async () => {
    try {
      const isMobile = otpType === 'mobile';
      const endpoint = isMobile ? sendMobileOtp : sendEmailOtp;
      const payload = isMobile
        ? { mobile_number: mobileNumber, purpose_for: 'registration' }
        : { email: emailAddress };
      const response = await axios.post(endpoint, payload, {
        headers: getAuthHeaders(),
      });
      const token = response.data?.data?.token;
      if (!token) throw new Error('Token not received from server');

      setOtpToken(token);

      setOtpResendKey(Date.now());
      successToast('OTP resent successfully');
    } catch (error) {
      console.error('Failed to resend OTP:', error);
      const errorMessage =
        error.response?.data?.error?.message ||
        (error.response?.status === 401
          ? 'Authentication failed: Please log in again'
          : 'Failed to resend OTP');
      if (error.response?.status === 401) navigate('/login');
      errorToast(errorMessage);
    }
  };

  const handleUploadClick = (field) => {
    setLoadingStates((prev) => ({ ...prev, [field]: true }));
    try {
      setUploadField(field);
      if (formikRef.current) {
        const { values } = formikRef.current;
        const files = field === 'aadhar_card_number'
          ? [
            values.front_aadhar_card_file ? { file: values.front_aadhar_card_file, preview: URL.createObjectURL(values.front_aadhar_card_file) } : null,
            values.back_aadhar_card_file ? { file: values.back_aadhar_card_file, preview: URL.createObjectURL(values.back_aadhar_card_file) } : null,
          ]
          : [
            values.front_pan_card_file ? { file: values.front_pan_card_file, preview: URL.createObjectURL(values.front_pan_card_file) } : null,
            values.back_pan_card_file ? { file: values.back_pan_card_file, preview: URL.createObjectURL(values.back_pan_card_file) } : null,
          ];
        setSelectedFiles(files);
      }
      setUploadModalOpen(true);
    } catch (error) {
      console.error('Failed to prepare upload:', error);
      errorToast('Failed to prepare upload');
      setLoadingStates((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleUploadSubmit = () => {
    if (!uploadField || !selectedFiles.some(file => file && file.file)) {
      errorToast('No file selected.');
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [uploadField]: true }));
    try {
      if (formikRef.current) {
        const { setFieldValue } = formikRef.current;
        if (uploadField === 'aadhar_card_number') {
          setFieldValue('front_aadhar_card_file', selectedFiles[0]?.file || null);
          setFieldValue('back_aadhar_card_file', selectedFiles[1]?.file || null);
        } else if (uploadField === 'pan_card_number') {
          setFieldValue('front_pan_card_file', selectedFiles[0]?.file || null);
          setFieldValue('back_pan_card_file', selectedFiles[1]?.file || null);
        }
      }
      setUploadedDocs((prev) => ({
        ...prev,
        [uploadField]: true,
      }));
      successToast(`Files uploaded for ${uploadField === 'aadhar_card_number' ? 'Aadhar' : 'PAN'}`);
      setUploadModalOpen(false);
    } catch (error) {
      errorToast('Failed to upload files');
    } finally {
      setLoadingStates((prev) => ({ ...prev, [uploadField]: false }));
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().when([], {
      is: () => step === 1,
      then: (schema) => schema.required('Name is required'),
    }),
    mobile_number: Yup.string().when([], {
      is: () => step === 1,
      then: (schema) =>
        schema
          .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits')
          .required('Mobile number is required'),
    }),
    email: Yup.string().when([], {
      is: () => step === 1,
      then: (schema) =>
        schema.trim().email('Invalid email').required('Email is required'),
    }),
    aadhar_card_number: Yup.string().when([], {
      is: () => step === 1,
      then: (schema) =>
        schema
          .required('Aadhar number is required')
          .matches(/^\d{12}$/, 'Aadhar number must be exactly 12 digits'),
    }),
    pan_card_number: Yup.string().when([], {
      is: () => step === 1,
      then: (schema) =>
        schema
          .required('PAN number is required')
          .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN number format'),
    }),
    qualification: Yup.string().when([], {
      is: () => step === 1,
      then: (schema) => schema.required('Qualification is required'),
    }),
    bank_ifsc_code: Yup.string().when([], {
      is: () => step === 2,
      then: (schema) =>
        schema
          .required('IFSC code is required')
          .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format'),
    }),
    bank_name: Yup.string().when([], {
      is: () => step === 2,
      then: (schema) => schema.required('Bank name is required'),
    }),
    bank_branch: Yup.string().when([], {
      is: () => step === 2,
      then: (schema) => schema.required('Bank branch is required'),
    }),
    bank_account_number: Yup.string().when([], {
      is: () => step === 2,
      then: (schema) =>
        schema
          .required('Account number is required')
          .matches(/^\d{9,18}$/, 'Account number must be between 9 and 18 digits'),
    }),
    front_aadhar_card_file: Yup.mixed().when([], {
      is: () => step === 2,
      then: (schema) => schema.required('Front Aadhar card is required'),
    }),
    back_aadhar_card_file: Yup.mixed().when([], {
      is: () => step === 2,
      then: (schema) => schema.required('Back Aadhar card is required'),
    }),
    front_pan_card_file: Yup.mixed().when([], {
      is: () => step === 2,
      then: (schema) => schema.required('Front PAN card is required'),
    }),
    back_pan_card_file: Yup.mixed().when([], {
      is: () => step === 2,
      then: (schema) => schema.required('Back PAN card is required'),
    }),
  });

  const isFilesValid = () => {
    if (!selectedFiles || selectedFiles.length === 0) return false;
    return selectedFiles.some((file) => file && file.file);
  };

  const handleSubmit = async (values) => {
    if (step === 1) {
      if (!verifiedFields.mobile_number || !verifiedFields.email) {
        errorToast('Please verify both mobile number and email');
        return;
      }
      setStep(2);
    } else {
      if (!selectedUser) {
        errorToast('Please select a user type.');
        return;
      }

      try {
        const formData = new FormData();
        const transformedValues = {
          ...values,
          join_as: selectedUser,
          qualification: values.qualification,
        };

        Object.entries(transformedValues).forEach(([key, value]) => {
          if (value) formData.append(key, value);
        });
        if (loggedInUser?.user?.advisor_id) {
          formData.append("advisor_id", loggedInUser?.user?.advisor_id);
          formData.append("bank_details_id", loggedInUser?.user?.bank_details?.[0]?.bank_details_id);
          const response = await axios.post(advisorReSubmitUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
          });
          setFormSubmitted(true)
          successToast('Registration re-submission successful!');
        } else {
          const response = await axios.post(advisorRegistration, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
          });
          setFormSubmitted(true)
          successToast('Registration successful!');
        }
      } catch (error) {
        console.error('Registration failed:', error);
        const errorMessage =
          error.response?.data?.error?.message ||
          (error.response?.status === 401
            ? 'Authentication failed: Please log in again'
            : 'Registration failed. Please try again.');
        if (error.response?.status === 401) navigate('/login');
        errorToast(errorMessage);
      }
    }
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
  };

  return (
    formSubmitted ? (
      <FormSubmissionPage />
    ) : (
      <div className='h-full overflow-auto'>
        <div className="p-4 page-container">
          <CustomPaperBox elevation="md" variant="default" className="p-6 space-y-6">
            <CustomPaperBox elevation="sm" variant="default" className="p-6 flex items-center justify-center">
              <div className="flex items-center justify-center gap-4">
                <div className="w-6 h-6 leading-6 flex items-center justify-center rounded-full bg-blue-600 text-white font-semibold text-sm">
                  {step}
                </div>
                <div className="text-md font-semibold">Onboarding</div>
                <CustomChip label="In Progress" variant="blue" size="sm" />
              </div>
            </CustomPaperBox>

            {(loggedInUser?.user?.advisor_onboarding_status_id?.title === "Rejected") && (
              <AdvisorConditionsPage type='RejectionForm' />
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={handleSubmit}
              validateOnChange
              validateOnBlur
              validationContext={{ step }}
              innerRef={formikRef}
            >
              {(formik) => (
                <>
                  <Form>
                    <UserSelection selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
                    {step === 1 && (
                      <BasicDetailsForm
                        formik={formik}
                        onNext={formik.handleSubmit}
                        onBack={handleBack}
                        handleVerifyClick={handleVerifyClick}
                        verifiedFields={verifiedFields}
                        verifyErrors={verifyErrors}
                        handleUploadClick={handleUploadClick}
                        loadingStates={loadingStates}
                        uploadedDocs={uploadedDocs}
                      />
                    )}
                    {step === 2 && (
                      <BankDetailsForm
                        formik={formik}
                        onNext={formik.handleSubmit}
                        onBack={handleBack}
                      />
                    )}
                  </Form>

                <OtpVerificationModal
                  key={otpResendKey}
                  open={otpModalOpen}
                  otpType={otpType}
                  mobileNumber={mobileNumber}
                  emailAddress={emailAddress}
                  onClose={() => setOtpModalOpen(false)}
                  onSubmit={handleOtpSubmit}
                  onResend={handleOtpResend}
                />

                  <UploadModal
                    open={uploadModalOpen}
                    uploadField={uploadField}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    onClose={() => {
                      setUploadModalOpen(false);
                    }}
                    onSubmit={handleUploadSubmit}
                    isFormValid={isFilesValid()}
                  />
                </>
              )}
            </Formik>
          </CustomPaperBox>
        </div>
      </div>
    )
  )
};

export default OnboardingForm;